import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import Swal from 'sweetalert2';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';
import {
  ICarePlanActivitiesDetailed,
  ICarePlanCode,
  ICarePlanDetailed,
  ICarePlanDropdown,
  ICarePlanLinkAppointmentRequest,
  ICarePlanLinkMedicationRequest,
  ICarePlanLinkServiceRequestRequest,
  ICarePlanRequest,
} from '../interfaces/CarePlan/ICarePlan';
import { ICarePlanAttachmentDetailed } from '../interfaces/CarePlan/ICarePlanAttachment';

@Injectable({ providedIn: 'root' })
export class CarePlanService {
  public carePlanModalSub = new BehaviorSubject<boolean>(false);
  public carePlanReadOnlySub = new BehaviorSubject<boolean>(false);
  public carePlanDirtySub = new BehaviorSubject<boolean>(false);
  public carePlanChangedSub = new Subject<void>();

  public carePlanEditSub = new BehaviorSubject<
    ICarePlanDetailed | undefined
  >(undefined);

  public carePlanDataSub = new BehaviorSubject<ICarePlanDetailed[]>([]);
  public activeCarePlanDataSub = new BehaviorSubject<ICarePlanDetailed[]>([]);
  public pastCarePlanDataSub = new BehaviorSubject<ICarePlanDetailed[]>([]);

  public carePlanLoadingSub = new BehaviorSubject<boolean>(false);
  public carePlanActivitiesLoadingSub = new BehaviorSubject<boolean>(false);
  public carePlanActivitiesSub =
    new BehaviorSubject<ICarePlanActivitiesDetailed | null>(null);

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  async getData() {
    this.carePlanLoadingSub.next(true);
    const patientId = this.patientService.patientIDsignal();
    const visitId = this.eocService.currentVisitId();

    forkJoin({
      summaryResponse: this.getCarePlan(patientId, visitId),
      activeResponse: this.getActiveCarePlans(patientId, visitId),
      pastResponse: this.getPastCarePlans(patientId, visitId),
      dropdownResponse: this.getDropdownData(),
    }).subscribe({
      next: ({
        summaryResponse,
        activeResponse,
        pastResponse,
        dropdownResponse,
      }) => {
        const summaryCarePlans = this.enrichTerminologyDisplays(
          summaryResponse?.data ?? [],
          dropdownResponse.data,
        );
        const activeCarePlans = this.enrichTerminologyDisplays(
          activeResponse?.data ?? [],
          dropdownResponse.data,
        );
        const pastCarePlans = this.enrichTerminologyDisplays(
          pastResponse?.data ?? [],
          dropdownResponse.data,
        );

        this.carePlanDataSub.next(summaryCarePlans);
        this.activeCarePlanDataSub.next(activeCarePlans);
        this.pastCarePlanDataSub.next(pastCarePlans);
        this.carePlanLoadingSub.next(false);
      },
      error: (err) => {
        this.carePlanLoadingSub.next(false);
        this.swalToastService.toastError('Unable to retrieve care plans');
        console.error('Error fetching care plans:', err);
      },
    });
  }

  getCarePlan(
    patientId: string | number,
    visitId?: string | number
  ): Observable<IGenericServiceResponse<ICarePlanDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const numericPatientId = Number(patientId);
    const numericVisitId = this.toNumber(visitId);

    if (numericVisitId !== undefined) {
      return this._http.get<IGenericServiceResponse<ICarePlanDetailed[]>>(
        `${environment.BACKEND_URL}CarePlan/${numericPatientId}?visitId=${numericVisitId}`,
        {
          headers: headers,
        }
      );
    } else {
      return this._http.get<IGenericServiceResponse<ICarePlanDetailed[]>>(
        `${environment.BACKEND_URL}CarePlan/${numericPatientId}`,
        {
          headers: headers,
        }
      );
    }
  }

  getActiveCarePlans(
    patientId: string | number,
    visitId?: string | number,
  ): Observable<IGenericServiceResponse<ICarePlanDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const numericPatientId = Number(patientId);
    const numericVisitId = this.toNumber(visitId);
    const url =
      numericVisitId !== undefined
        ? `${environment.BACKEND_URL}CarePlan/ActiveCarePlans/${numericPatientId}?visitId=${numericVisitId}`
        : `${environment.BACKEND_URL}CarePlan/ActiveCarePlans/${numericPatientId}`;

    return this._http.get<IGenericServiceResponse<ICarePlanDetailed[]>>(url, {
      headers,
    });
  }

  getPastCarePlans(
    patientId: string | number,
    visitId?: string | number,
  ): Observable<IGenericServiceResponse<ICarePlanDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const numericPatientId = Number(patientId);
    const numericVisitId = this.toNumber(visitId);
    const url =
      numericVisitId !== undefined
        ? `${environment.BACKEND_URL}CarePlan/PastCarePlans/${numericPatientId}?visitId=${numericVisitId}`
        : `${environment.BACKEND_URL}CarePlan/PastCarePlans/${numericPatientId}`;

    return this._http.get<IGenericServiceResponse<ICarePlanDetailed[]>>(url, {
      headers,
    });
  }

  getDropdownData(): Observable<
    IGenericServiceResponse<ICarePlanDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<ICarePlanDropdown>>(
      `${environment.BACKEND_URL}CarePlan/DropdownData`,
      { headers: headers }
    );
  }

  addCarePlan(
    carePlanObj: ICarePlanRequest
  ): Observable<IGenericServiceResponse<number | null>> {
    const request: ICarePlanRequest = {
      ...carePlanObj,
      visitId: this.toNumber(this.eocService.currentVisitId()),
      patientId: this.toNumber(this.patientService.patientIDsignal()),
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IGenericServiceResponse<number | null>>(
      `${environment.BACKEND_URL}CarePlan`,
      request,
      { headers: headers }
    );
  }

  editCarePlan(
    carePlanObj: ICarePlanRequest
  ): Observable<IGenericServiceResponse<number | null>> {
    const request: ICarePlanRequest = {
      ...carePlanObj,
      visitId: this.toNumber(this.eocService.currentVisitId()),
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put<IGenericServiceResponse<number | null>>(
      `${environment.BACKEND_URL}CarePlan/${carePlanObj.id}`,
      request,
      {
        headers: headers,
      }
    );
  }

  deleteCarePlan(
    carePlanId: string | number,
    deleteLinkedDraftRecords?: boolean,
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const query =
      deleteLinkedDraftRecords === undefined
        ? ''
        : `?deleteLinkedDraftRecords=${deleteLinkedDraftRecords}`;

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}CarePlan/${Number(carePlanId)}${query}`,
      {
        headers: headers,
      }
    );
  }

  reapplyCarePlan(carePlanId: string | number): Observable<IServiceResponse> {
    return this.deleteCarePlan(carePlanId);
  }

  stopCarePlan(
    carePlanId: string | number,
    visitId: string | number,
  ): Observable<IGenericServiceResponse<number>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put<IGenericServiceResponse<number>>(
      `${environment.BACKEND_URL}CarePlan/Stop/${Number(carePlanId)}/${Number(
        visitId,
      )}`,
      {},
      {
        headers,
      },
    );
  }

  getActivities(
    carePlanId: string | number
  ): Observable<IGenericServiceResponse<ICarePlanActivitiesDetailed>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<ICarePlanActivitiesDetailed>>(
      `${environment.BACKEND_URL}CarePlan/${Number(carePlanId)}/Activities`,
      {
        headers,
      }
    );
  }

  linkAppointmentActivity(
    carePlanId: string | number,
    request: ICarePlanLinkAppointmentRequest
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IServiceResponse>(
      `${environment.BACKEND_URL}CarePlan/${Number(
        carePlanId
      )}/Activities/Appointments/Link`,
      request,
      {
        headers,
      }
    );
  }

  linkMedicationActivity(
    carePlanId: string | number,
    request: ICarePlanLinkMedicationRequest
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IServiceResponse>(
      `${environment.BACKEND_URL}CarePlan/${Number(
        carePlanId
      )}/Activities/Medications/Link`,
      request,
      {
        headers,
      }
    );
  }

  linkServiceRequestActivity(
    carePlanId: string | number,
    request: ICarePlanLinkServiceRequestRequest
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IServiceResponse>(
      `${environment.BACKEND_URL}CarePlan/${Number(
        carePlanId
      )}/Activities/ServiceRequests/Link`,
      request,
      {
        headers,
      }
    );
  }

  deleteActivity(activityLinkId: string | number): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}CarePlan/Activities/${Number(activityLinkId)}`,
      {
        headers,
      }
    );
  }

  getCarePlanAttachments(
    carePlanId: string | number,
  ): Observable<IGenericServiceResponse<ICarePlanAttachmentDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<ICarePlanAttachmentDetailed[]>>(
      `${environment.BACKEND_URL}CarePlan/${Number(carePlanId)}/Attachments`,
      {
        headers,
      },
    );
  }

  uploadCarePlanAttachment(
    carePlanId: string | number,
    file: File,
    date?: string | Date | null,
    description?: string | null,
  ): Observable<IGenericServiceResponse<ICarePlanAttachmentDetailed>> {
    const formData = new FormData();
    formData.append('file', file);

    if (date) {
      const normalizedDate =
        typeof date === 'string'
          ? date
          : new Date(date).toISOString().split('T')[0];
      formData.append('date', normalizedDate);
    }

    if (description?.trim()) {
      formData.append('description', description.trim());
    }

    return this._http.post<IGenericServiceResponse<ICarePlanAttachmentDetailed>>(
      `${environment.BACKEND_URL}CarePlan/${Number(carePlanId)}/Attachments`,
      formData,
    );
  }

  downloadCarePlanAttachment(
    carePlanId: string | number,
    attachmentId: string | number,
  ): Observable<HttpResponse<Blob>> {
    return this._http.get(
      `${environment.BACKEND_URL}CarePlan/${Number(
        carePlanId,
      )}/Attachments/${Number(attachmentId)}`,
      {
        observe: 'response',
        responseType: 'blob',
      },
    );
  }

  deleteCarePlanAttachment(
    carePlanId: string | number,
    attachmentId: string | number,
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}CarePlan/${Number(
        carePlanId,
      )}/Attachments/${Number(attachmentId)}`,
      {
        headers,
      },
    );
  }

  deleteCarePlanFromTable(carePlanId: string) {
    this.carePlanDataSub.next(
      this.carePlanDataSub.value.filter(
        (carePlan) => carePlan.id.toString() !== carePlanId
      )
    );
    this.activeCarePlanDataSub.next(
      this.activeCarePlanDataSub.value.filter(
        (carePlan) => carePlan.id.toString() !== carePlanId,
      ),
    );
    this.pastCarePlanDataSub.next(
      this.pastCarePlanDataSub.value.filter(
        (carePlan) => carePlan.id.toString() !== carePlanId,
      ),
    );
  }

  resetEditorState() {
    this.carePlanDirtySub.next(false);
    this.carePlanReadOnlySub.next(false);
    this.carePlanModalSub.next(false);
    this.carePlanEditSub.next(undefined);
  }

  notifyCarePlanChanged() {
    this.carePlanChangedSub.next();
  }

  async confirmDiscardUnsavedChanges(): Promise<boolean> {
    if (!this.carePlanDirtySub.value) {
      return true;
    }

    const result = await Swal.fire({
      icon: 'warning',
      text: this.translate.instant(
        'You have unsaved changes in the care plan. Discard them and continue?'
      ),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('Discard changes'),
      cancelButtonText: this.translate.instant('Stay'),
    });

    if (!result.isConfirmed) {
      return false;
    }

    this.resetEditorState();
    return true;
  }

  enrichTerminologyDisplays(
    carePlans: ICarePlanDetailed[],
    dropdownData: ICarePlanDropdown
  ): ICarePlanDetailed[] {
    return carePlans.map((carePlan) => ({
      ...carePlan,
      isStopped: carePlan.isStopped ?? !!carePlan.replacementId,
      ...this.getTerminologyFields(
        'status',
        carePlan.statusId,
        carePlan.statusDisplay,
        dropdownData.statuses
      ),
      ...this.getTerminologyFields(
        'intent',
        carePlan.intentId,
        carePlan.intentDisplay,
        dropdownData.intents
      ),
      ...this.getTerminologyFields(
        'category',
        carePlan.categoryId,
        carePlan.categoryDisplay,
        dropdownData.categories
      ),
    }));
  }

  private getTerminologyFields(
    field: 'status' | 'intent' | 'category',
    id: number | null,
    currentDisplay: string | null,
    options: ICarePlanCode[]
  ): Record<string, string | null> {
    const option = options.find((item) => item.id === Number(id));

    return {
      [`${field}Code`]: option?.code ?? null,
      [`${field}System`]: option?.system ?? null,
      [`${field}Display`]: currentDisplay || option?.display || null,
    };
  }

  private toNumber(
    value: string | number | null | undefined
  ): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
}
